import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { ampli } from '~entities/ampli';
import type { FindApplicationsRequestParams } from '~entities/application';
import { useMerchantsListQuery } from '~entities/merchant';
import { useGetPointsOfSalesQuery } from '~entities/pos';

import type { DataTableSorting } from '~shared/ui/data-table';
import { useColumnVisibility } from '~shared/ui/data-table';

import {
  getDefaultApplicationsFilter,
  persistApplicationsFilters,
  restoreApplicationsFilters,
  type ApplicationsFiltersValue,
} from './applications-filters';
import { useApplicationsTableDataQuery } from './applications-table-queries';

import { ALL_COLUMN_IDS } from '../lib/columns';
import { filtersToParams, sortToParams } from '../lib/search-params-converters';
import { DEFAULT_SORTING, defaultPagination } from '../lib/table-options';

const COLUMN_VISIBILITY_STORAGE_KEY = 'applications-table:column-visibility';

const MIN_MERCHANTS_TO_FILTER = 2;

export function useApplicationsTable() {
  const methods = useForm<ApplicationsFiltersValue>({
    defaultValues: getDefaultApplicationsFilter(),
  });

  const [appliedFilters, setAppliedFilters] =
    useState<ApplicationsFiltersValue>();
  const [pagination, setPagination] = useState(defaultPagination);
  const [sorting, setSorting] = useState<DataTableSorting>(DEFAULT_SORTING);
  const { visibleColumns, setVisibleColumns, columnVisibility } =
    useColumnVisibility(ALL_COLUMN_IDS, {
      storageKey: COLUMN_VISIBILITY_STORAGE_KEY,
    });

  const {
    data: merchantsList,
    isError: isMerchantsError,
    isSuccess: isMerchantsSuccess,
  } = useMerchantsListQuery();
  const { data: posList, isSuccess: isPosSuccess } = useGetPointsOfSalesQuery();

  const trackFilterApplied = useCallback(
    (filters: ApplicationsFiltersValue) => {
      const { startDate, endDate } = filters.period ?? {};

      ampli.applicationsScreenFilterApplied({
        filter_period: startDate && endDate ? `${startDate}-${endDate}` : '',
        filter_chosenMerchantsCount: filters.merchantIds.length,
        filter_chosenStoresCount: filters.posIds.length,
        filter_statuses: filters.statuses,
      });
    },
    []
  );

  const resetPage = () =>
    setPagination((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));

  const applyFilters = useCallback(() => {
    const filters = methods.getValues();
    setAppliedFilters(filters);
    persistApplicationsFilters(filters);
    trackFilterApplied(filters);
    resetPage();
  }, [methods, trackFilterApplied]);

  const resetFilters = useCallback(() => {
    const defaults = getDefaultApplicationsFilter();
    methods.reset(defaults);
    setAppliedFilters(defaults);
    persistApplicationsFilters(defaults);
    trackFilterApplied(defaults);
    resetPage();
  }, [methods, trackFilterApplied]);

  const handleSortingChange = (newSorting: DataTableSorting) => {
    setSorting(newSorting);
    resetPage();
  };

  const isHydratedRef = useRef(false);
  useEffect(() => {
    if (isHydratedRef.current || !isMerchantsSuccess || !isPosSuccess) {
      return;
    }

    isHydratedRef.current = true;
    const restored = restoreApplicationsFilters(merchantsList, posList);
    methods.reset(restored);
    setAppliedFilters(restored);
    persistApplicationsFilters(restored);
    trackFilterApplied(restored);
  }, [
    isMerchantsSuccess,
    isPosSuccess,
    merchantsList,
    posList,
    methods,
    trackFilterApplied,
  ]);

  useEffect(() => {
    if (isMerchantsError || isMerchantsSuccess) {
      ampli.applicationsScreenShown({ merchantsCount: merchantsList?.length });
    }
  }, [isMerchantsError, isMerchantsSuccess, merchantsList]);

  const searchParams = useMemo<FindApplicationsRequestParams | undefined>(
    () =>
      appliedFilters
        ? {
            page: pagination.page,
            perPage: pagination.perPage,
            sort: sortToParams(sorting),
            filter: filtersToParams(appliedFilters),
          }
        : undefined,
    [appliedFilters, pagination, sorting]
  );

  const { data, isLoading } = useApplicationsTableDataQuery(searchParams);
  const applications = data?.applications ?? [];
  const total = data?.total ?? 0;

  const hideMerchantFilter =
    !isMerchantsSuccess || merchantsList.length < MIN_MERCHANTS_TO_FILTER;

  const clearSelectFilters = useCallback(() => {
    methods.setValue('statuses', []);
    methods.setValue('merchantIds', []);
    methods.setValue('posIds', []);
  }, [methods]);

  const activeFiltersCount =
    (appliedFilters?.statuses.length ? 1 : 0) +
    (!hideMerchantFilter && appliedFilters?.merchantIds.length ? 1 : 0) +
    (appliedFilters?.posIds.length ? 1 : 0);

  return {
    methods,
    applications,
    total,
    isLoading,
    pagination,
    sorting,
    searchParams,
    hideMerchantFilter,
    applyFilters,
    resetFilters,
    clearSelectFilters,
    activeFiltersCount,
    onSortingChange: handleSortingChange,
    visibleColumns,
    onVisibleColumnsChange: setVisibleColumns,
    columnVisibility,
    onPageChange: (page: number) =>
      setPagination((prev) => ({ ...prev, page })),
    onPageSizeChange: (perPage: number) => setPagination({ page: 0, perPage }),
  };
}
